import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import {doppeViewerGetHeadlessToken} from './doppe-viewer-headless-client-service/doppe-viewer-headless-client-service-utils';
import {suspenseAsyncValueCreate, suspenseAsyncValueCreateWithParams} from '@wix/devzai-utils-react';
import {cachedValueCreateWithParamsV2, hashFunctionCreate, stringIsNotNullableOrWhiteSpace} from '@wix/devzai-utils-common';
import {WixAppsIds, WixBlogApiPostInfo, WixStoresApiProductInfo} from '@wix/devzai-common-wix';
import type {DoppeDtoBlogPost} from '../../client-server-common/types/doppe-dto-blog-post';
import type {DoppeDtoBookingService} from '../../client-server-common/types/doppe-dto-booking-service';
import type {DoppeDtoBookingServiceType} from '../../client-server-common/types/doppe-dto-booking-service';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import type {DoppeDtoEventRegistrationType} from '../../client-server-common/types/doppe-dto-event';
import type {DoppeDtoStoreProduct} from '../../client-server-common/types/doppe-dto-store-product';
import type {DoppeRecommendProductsType} from '../../client-server-common/types/doppe-recommend-products-type/doppe-recommend-products-type';
import type {DoppeViewerCategoryMenuItem} from '../../client-server-common/api/doppe-viewer-api';
import {DoppeDtoInnerPageVerticalListPagesIds} from '../../client-server-common/types/doppe-dto-inner-page';
import {
    BymoPageNavigationMenuItemSettings,
    BymoPageNavigationMenuItemType,
    type BymoPagePropsForViewer,
    bymoPageGetNavigationMenuItems,
} from '../../client-server-common/bymo-pages';
import {
    bymoPageNavigationMenuItemIsVerticalListPage,
} from '../../client-server-common/bymo-pages/bymo-page-navigation-settings';
import {
    doppeViewerAsyncListFactoryBlogPosts,
    doppeViewerAsyncListFactoryBlogPostsByCategory,
    doppeViewerAsyncListFactoryEvents,
    doppeViewerAsyncListFactoryEventsByCategory,
    doppeViewerAsyncListFactoryProducts,
    doppeViewerAsyncListFactoryProductsByCategory,
    doppeViewerAsyncListFactoryServices,
    doppeViewerAsyncListFactoryServicesByCategory,
} from "../../components/doppe-viewer-async-list-factories";

export class DoppeViewerDataCacheService extends DoppeViewerAppClientServiceBase {

    /**
     * Suspense cache for blog post data by slug.
     * Returns the blog post if found, or null if not found or no slug provided.
     */
    public readonly blogPostBySlug = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            blogPostSlug: hashFunctionCreate<string>(),
        }),
        async (params: {
            blogPostSlug: string;
        }): Promise<WixBlogApiPostInfo | null> => {
            const {blogPostSlug} = params;
            
            // If we have a slug, fetch the real blog post
            if (stringIsNotNullableOrWhiteSpace(blogPostSlug)) {
                const postInfo = await this.apiClient.getBlogPost({slug: blogPostSlug});
                if (postInfo) {
                    return postInfo;
                }
            }

            // No slug or not found - return null
            return null;
        }
    );

    /**
     * Suspense cache for store product data by slug.
     * Returns the product if found, or null if not found or no slug provided.
     * Shared by both physical and digital product renderers.
     */
    public readonly storeProductBySlug = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            productSlug: hashFunctionCreate<string>(),
        }),
        async (params: {
            productSlug: string;
        }): Promise<WixStoresApiProductInfo | null> => {
            const {productSlug} = params;
            
            // If we have a slug, fetch the real product
            if (stringIsNotNullableOrWhiteSpace(productSlug)) {
                const productInfo = await this.apiClient.getStoreProductInfo({slug: productSlug});
                if (productInfo) {
                    return productInfo;
                }
            }

            // No slug or not found - return null
            return null;
        }
    );

    /**
     * Suspense cache for fetching related blog posts by their IDs.
     * Used by the related posts section on the blog post item page.
     */
    public readonly relatedBlogPosts = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            postIds: hashFunctionCreate<string>(),
        }),
        async (params: {
            postIds: string;
        }): Promise<DoppeDtoBlogPost[]> => {
            const ids = params.postIds.split(',').filter(id => id.length > 0);

            if (ids.length === 0) {
                return [];
            }

            const result = await this.apiClient.getBlogPosts({
                ids: ids,
                skip: 0,
                take: ids.length,
            });

            return result.results;
        }
    );

    /**
     * Suspense cache for fetching the most recent blog posts.
     * Used as a fallback when a blog post has no relatedPostIds.
     */
    public readonly recentBlogPosts = suspenseAsyncValueCreate(
        async (): Promise<DoppeDtoBlogPost[]> => {
            const result = await this.apiClient.getBlogPosts({
                skip: 0,
                take: 4,
            });
            return result.results;
        }
    );

    /**
     * Suspense cache for fetching recent booking services, optionally filtered by service type.
     * Used by the recommended services section on the service item page.
     */
    public readonly recentBookingServices = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            serviceType: hashFunctionCreate<string>(),
        }),
        async (params: {
            serviceType: string;
        }): Promise<DoppeDtoBookingService[]> => {
            const serviceType = params.serviceType || undefined;
            const result = await this.apiClient.getBookingServices({
                serviceType: serviceType as DoppeDtoBookingServiceType | undefined,
                skip: 0,
                take: 11,
            });
            return result.results;
        }
    );

    /**
     * Suspense cache for fetching recent events, optionally filtered by registration type.
     * Used by the recommended events section on the event item page.
     * Includes ended events so recommendations appear even when all events are in the past.
     */
    public readonly recentEvents = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            registrationType: hashFunctionCreate<string>(),
        }),
        async (params: {
            registrationType: string;
        }): Promise<DoppeDtoEvent[]> => {
            const registrationType = params.registrationType || undefined;
            const result = await this.apiClient.getEvents({
                registrationType: registrationType as DoppeDtoEventRegistrationType | undefined,
                includeEndedEvents: true,
                skip: 0,
                take: 4,
            });
            return result.results;
        }
    );

    /**
     * Suspense cache for fetching recent store products.
     * Used as a fallback when the recommendations API is unavailable.
     */
    public readonly recentStoreProducts = suspenseAsyncValueCreate(
        async (): Promise<DoppeDtoStoreProduct[]> => {
            const result = await this.apiClient.getStoreProducts({
                skip: 0,
                take: 11,
            });
            return result.results;
        }
    );

    /**
     * Suspense cache for fetching recommended products via the Wix E-commerce Recommendations API.
     * Calls the headless SDK on the client to get recommended product IDs,
     * then fetches full product data from the server.
     * Keyed by productId + algorithmType to avoid redundant API calls.
     */
    public readonly recommendedProducts = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            productId: hashFunctionCreate<string>(),
            algorithmType: hashFunctionCreate<string>(),
        }),
        async (params: {
            productId: string;
            algorithmType: DoppeRecommendProductsType;
        }): Promise<DoppeDtoStoreProduct[]> => {
            const clientId = this.clientServices.headlessService.getClientIdOrNull();

            if (!clientId) {
                return [];
            }

            const {doppeViewerHeadlessSdkGetRecommendedProductIds} =
                await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-recommendations.dynamic');

            const productIds = await doppeViewerHeadlessSdkGetRecommendedProductIds({
                clientId: clientId,
                productId: params.productId,
                algorithmType: params.algorithmType,
            });

            if (productIds.length === 0) {
                return [];
            }

            const result = await this.apiClient.getStoreProducts({
                ids: productIds,
                take: productIds.length,
            });

            return result.results;
        }
    );

    public readonly cachedStoreProductsList = doppeViewerAsyncListFactoryProducts(() => this.apiClient);

    public readonly cachedStoreProductsByCategorySlugList = doppeViewerAsyncListFactoryProductsByCategory(() => this.apiClient);

    public readonly cachedBlogPostsList = doppeViewerAsyncListFactoryBlogPosts(() => this.apiClient);

    public readonly cachedBlogPostsByCategorySlugList = doppeViewerAsyncListFactoryBlogPostsByCategory(() => this.apiClient);

    public readonly cachedEventsList = doppeViewerAsyncListFactoryEvents(() => this.apiClient);

    public readonly cachedEventsByCategorySlugList = doppeViewerAsyncListFactoryEventsByCategory(
        () => this.apiClient,
        async (name) => {
            const clientId = this.clientServices.headlessService.getClientIdOrNull();

            if (!clientId) {
                return null;
            }

            const {
                doppeViewerHeadlessSdkGetEventCategoryByName,
            } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-event-categories.dynamic');

            const category = await doppeViewerHeadlessSdkGetEventCategoryByName({
                clientId: clientId,
                tokens: doppeViewerGetHeadlessToken(this.clientServices),
                name: name,
            });

            return category?.id ?? null;
        },
    );

    public readonly cachedServicesList = doppeViewerAsyncListFactoryServices(() => this.apiClient);

    public readonly cachedServicesByCategorySlugList = doppeViewerAsyncListFactoryServicesByCategory(() => this.apiClient);

    public readonly navigationMenuCategoriesByPageId = cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<{ pageId: string }>({
            pageId: hashFunctionCreate(),
        }),
        valueFunction: (params): Promise<DoppeViewerCategoryMenuItem[]> => {
            return this.fetchNavigationMenuCategoriesImpl(params.pageId);
        }
    });

    public preloadNavigationMenuCategories(bymoPageProps: BymoPagePropsForViewer): void {
        const navigationItems = bymoPageGetNavigationMenuItems(bymoPageProps);

        for (const item of navigationItems) {
            if (item.settings.type === BymoPageNavigationMenuItemType.Page) {
                const pageSettings = item.settings as BymoPageNavigationMenuItemSettings.Page;
                if (pageSettings.showCategoriesInMenu && bymoPageNavigationMenuItemIsVerticalListPage(pageSettings.pageId)) {
                    this.navigationMenuCategoriesByPageId.getValue({pageId: pageSettings.pageId});
                }
            }
        }
    }

    private async fetchNavigationMenuCategoriesImpl(pageId: string): Promise<DoppeViewerCategoryMenuItem[]> {
        switch (pageId) {
            case DoppeDtoInnerPageVerticalListPagesIds.Events: {
                const headlessClientId = this.clientServices.headlessService.getClientIdOrNull();
                if (!headlessClientId) {
                    return [];
                }
                const {
                    doppeViewerHeadlessSdkQueryEventCategories,
                } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-event-categories.dynamic');
                const categories = await doppeViewerHeadlessSdkQueryEventCategories({
                    clientId: headlessClientId,
                    tokens: doppeViewerGetHeadlessToken(this.clientServices),
                });
                return categories.map(cat => ({
                    name: cat.name,
                    slug: cat.name,
                }));
            }
            case DoppeDtoInnerPageVerticalListPagesIds.Products:
                return this.apiClient.getStoreCategories({});
            case DoppeDtoInnerPageVerticalListPagesIds.BlogPosts:
                return this.apiClient.getBlogCategories({});
            case DoppeDtoInnerPageVerticalListPagesIds.Services:
                return this.apiClient.getBookingCategories({});
            default:
                return [];
        }
    }

    public invalidateCreatorsMetaSiteWixTpaData (appDefId: string) {
        switch (appDefId) {
            case WixAppsIds.Stores:
            case WixAppsIds.StoresNew: {
                this.cachedStoreProductsList.invalidateAll();
                this.cachedStoreProductsByCategorySlugList.invalidateAll();
                break;
            }
            case WixAppsIds.Blog: {
                this.cachedBlogPostsList.invalidateAll();
                this.cachedBlogPostsByCategorySlugList.invalidateAll();
                break;
            }
            case WixAppsIds.Events: {
                this.cachedEventsList.invalidateAll();
                this.cachedEventsByCategorySlugList.invalidateAll();
                break;
            }
            case WixAppsIds.Booking: {
                this.cachedServicesList.invalidateAll();
                this.cachedServicesByCategorySlugList.invalidateAll();
                break;
            }
        }
    }
}
